import {Text} from "@gs-ux-uitoolkit-react/text";
import {Form, FormFeedback, FormControl} from "@gs-ux-uitoolkit-react/form";
import {Input} from "@gs-ux-uitoolkit-react/input";
import {Button} from "@gs-ux-uitoolkit-react/button";
import React, {useState, useEffect, useCallback} from "react";
import {useFormik} from "formik";
import {LoadingIcon} from "@gs-ux-uitoolkit-react/loading";
import {Alert} from '@gs-ux-uitoolkit-react/alert';
import {Radio, RadioChangeEvent} from '@gs-ux-uitoolkit-react/radio';
import {SignInPageLayout} from "./SignInPageLayout";

interface FormValue {
    username: string;
    last4ssn: string;
    dob: string;
    phonenumber: string
}

export function ForgotPasswordForm() {

    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState<string>();
    const [isLocked, setIsLocked] = useState<boolean>();
    const [displayText, setDisplayText] = useState<string>();
    const [isThirdPartyAgent, setisThirdPartyAgent] = useState<boolean>(false);
    const [mfaType, setMfaType] = useState<string>("sms");
    const [isUsernameComplete, setIsUsernameComplete] = useState(false);

    const initialValues = {
        username: '',
        last4ssn: '',
        dob: '',
        phonenumber: ''
    };

    const disabled = busy;

    useEffect(() => {
        let isLocked = (sessionStorage.getItem('isLocked') === 'true');
        setIsLocked(isLocked)
        if (isLocked){
            setDisplayText('Your access has been locked because you have made 3 invalid attempts to log in. To reset your password and restore access, please enter the following information.')
        } else {
            setDisplayText('To reset your password, please enter the following information.')
        }
    }, [])

    const formik = useFormik<FormValue>({
        initialValues,
        onSubmit: async (values, {setValues}) => {
            setBusy(true);
            try {
                values.username = values.username.trim().replace(/[&\/\\#+()$~%'":*?<>{}]/g, '');
                values.phonenumber = values.phonenumber.trim().replace(/[-]/g, '');
                if (isThirdPartyAgent){
                    await submitForgotPasswordThirdPartyAgent(values.username, values.phonenumber, mfaType);
                } else {
                    await submitForgotPassword(values.username, values.last4ssn, values.dob);
                }
                setValues(initialValues);
            } finally {
                setBusy(false);
            }
        }
    });


    const submitForgotPassword = useCallback(async (loginId: string, lastFourSSN: string, dateOfBirth: string) => {
        let url = `/api/v2/sign-in-attempts/forgot-password`;
        loginId = loginId.trim()
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({loginId, lastFourSSN, dateOfBirth}),
        });

        if (response.ok) {
            const body = await response.json();
            setMessage(body.message)
        } else if (response.status === 504) {
            setMessage('The server failed to respond');
        } else if (response.status === 403) {
            const body = await response.json();
            setMessage(body.displayMessage);
        } else if (response.status >= 400 && response.status < 500) {
            setMessage('Error: Some of the information provided below is not valid. Please try again.');
        } else if (response.status >= 500) {
            setMessage('The server is unavailable right now, please try again later');
        } else {
            const body = await response.json();
            setMessage(body.displayMessage);
        }
    }, []);


    //todo
    const submitForgotPasswordThirdPartyAgent = useCallback(async (loginId: string, phoneNumber: string, mfaType: string) => {
        let url = `/api/v2/sign-in-attempts/forgot-password/third-party-agent`;
        loginId = loginId.trim()
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({loginId, phoneNumber, mfaType}),
        });
        const body = await response.json();
        if (response.ok) {

            setMessage(body.message)
            if (body.isMfaSecretSent){
                let id = body.forgotPasswordAttemptId
                let token = body.token
                window.location.href = "/sign-in/forgot-password-continuation?attemptId=" + id + "&token=" + token
            }
        } else if (response.status === 504) {
            setMessage('The server failed to respond');
        } else if (response.status === 403) {
            setMessage(body.displayMessage);
        } else if (response.status >= 400 && response.status < 500) {
            setMessage(body.validationErrors[0].displayMessage);
        } else if (response.status >= 500) {
            setMessage('The server is unavailable right now, please try again later');
        } else {
            setMessage(body.displayMessage);
        }
    }, []);

    const getPmcode = useCallback(async (loginId: string) => {
        loginId = loginId.trim()
        let url = `/api/v2/sign-in-attempts/` + loginId +  `/pmcode`;
        const response = await fetch(url);

        if (response.ok) {
            let body = await response.text();
            body = body.trim()
            if (body !== '') {
                setisThirdPartyAgent(true)
            } else {
                setisThirdPartyAgent(false)
            }

        } else if (response.status === 504) {
            setMessage('The server failed to respond');
        } else if (response.status >= 400 && response.status < 500) {
            setMessage('Error: Some of the information provided below is not valid. Please try again.');
        } else if (response.status >= 500) {
            setMessage('The server is unavailable right now, please try again later');
        } else {
            const body = await response.json();
            setMessage(body.displayMessage);
        }
    }, []);

    function onChange(event: RadioChangeEvent) {
        const { value } = event.target as HTMLInputElement;
        setMfaType(value)
    }

    const formContent = (
        <div className="flex flex-col items-stretch gap-6 w-full">
            <Text typography="textTitle02" data-qa-comp="forgot-password-title">Reset your password</Text>
            <Text typography="textBody01Medium" color="colorTextNeutralBold">{displayText}</Text>
            { message &&
                <Alert status="warning">
                    {message}
                </Alert>
            }
            <Form className="flex flex-col gap-4 w-full" preventSubmit>
                <FormControl status={formik.touched.username && formik.errors.username ? 'error' : 'none'}>
                    <Input
                        autoFocus
                        placeholder="ex: johnsmith1"
                        name="username"
                        type="text"
                        size="md"
                        emphasis="regular"
                        label="Username"
                        className="w-full"
                        disabled={disabled}
                        onChange={formik.handleChange}
                        onBlur={(e) => {
                            formik.handleBlur(e);
                            if (e.target.value.length > 0) {
                                setIsUsernameComplete(true);
                                getPmcode(formik.values.username)
                            }
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                            }
                        }}
                        value={formik.values.username}
                        data-qa-comp="username-input"
                    />
                    <FormFeedback>{formik.touched.username ? formik.errors.username : null}</FormFeedback>
                </FormControl>
                { isUsernameComplete && !isThirdPartyAgent &&
                <FormControl status={formik.touched.last4ssn && formik.errors.last4ssn ? 'error' : 'none'}>
                    <Input
                        placeholder="****"
                        name="last4ssn"
                        type="password"
                        size="md"
                        emphasis="regular"
                        label="Last 4 digits of Social Security Number"
                        className="w-full"
                        disabled={disabled}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.last4ssn}
                        data-qa-comp="last4ssn-input"
                    />
                    <FormFeedback>{formik.touched.last4ssn ? formik.errors.last4ssn : null}</FormFeedback>
                </FormControl> }
                { isUsernameComplete && !isThirdPartyAgent &&
                <FormControl>
                    <Input
                        placeholder="mm/dd/yyyy"
                        name="dob"
                        type="date"
                        size="md"
                        emphasis="regular"
                        label="Date of birth"
                        className="w-full"
                        disabled={disabled}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.dob}
                        data-qa-comp="dob-input"
                    />
                    <FormFeedback></FormFeedback>
                </FormControl>
                }
                { isUsernameComplete && isThirdPartyAgent &&
                <FormControl status={formik.touched.phonenumber && formik.errors.phonenumber ? 'error' : 'none'}>
                    <Input
                        autoFocus
                        placeholder="***-***-****"
                        name="phonenumber"
                        type="text"
                        size="md"
                        emphasis="regular"
                        label="Phone number"
                        className="w-full"
                        disabled={disabled}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.phonenumber}
                        data-qa-comp="phone-input"
                    />
                    <FormFeedback>{formik.touched.phonenumber ? formik.errors.phonenumber : null}</FormFeedback>
                </FormControl>
                }
                { isUsernameComplete && isThirdPartyAgent &&
                    <FormControl>
                        <Radio name="radio1" value="sms" onChange={onChange} inline defaultChecked>
                            Receive SMS Pin
                        </Radio>
                        <Radio name="radio1" value="call" onChange={onChange} inline>
                            Receive Voicemail Pin
                        </Radio>
                    </FormControl>
                }
                <Button
                    className="w-full"
                    size="md"
                    appearance="highlight"
                    emphasis="bold"
                    type="submit"
                    onClick={formik.submitForm}
                    disabled={disabled}
                    data-qa-comp="reset-password-button"
                >
                    {!busy ? (isThirdPartyAgent ? 'Continue' : 'Reset Password') : <LoadingIcon size="sm"/>}
                </Button>
            </Form>
        </div>
    );

    return <SignInPageLayout showAgreement>{formContent}</SignInPageLayout>;
}